import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { UpdateAuthConfigBody } from './type';
import { clientFetch } from '~/lib/.client/fetch';
import { projectStore } from '~/lib/stores/project';
import { invariant } from '~/utils/invariant';

const getAuthConfig = async (projectId?: string) => {
  invariant(projectId);
  const response = await clientFetch(`/api/supabase/projects/${projectId}/authConfig`);
  const parsedReponse = await response.json();
  return parsedReponse;
};

export const useGetAuthConfig = () => {
  const projectId = projectStore.projectId.get();
  return useQuery({
    queryKey: ['auth-config', projectId],
    queryFn: () => getAuthConfig(projectId),
    enabled: !!projectId,
    retry: false,
  });
};

const updateAuthConfig = async ({ payload }: { payload: UpdateAuthConfigBody }) => {
  const projectId = projectStore.projectId.get();
  invariant(projectId);
  const response = await clientFetch(`/api/supabase/projects/${projectId}/authConfig`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data: { error?: string } = await response.json();

  if (data.error) {
    throw new Error(data.error);
  }

  return response;
};

export const useUpdateAuthConfig = () => {
  const projectId = projectStore.projectId.get();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAuthConfig,
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: ['auth-config', projectId],
      });
    },
    onError: (error: { message?: string }) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'There was a problem updating your auth config.';
      toast.error(message);
    },
  });
};
